import {
  Button,
  Classes,
  Icon,
  IconSize,
  InputGroup,
  Intent,
  NonIdealState,
  Spinner,
  SpinnerSize,
  Tag
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { ParentSize } from '@visx/responsive'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CellMeasurer, CellMeasurerCache, List } from 'react-virtualized'
import { requestInventoryLocations } from '../../../current/actions'
import { buildInventoryLocationOptionsIndexNameOnly } from '../../../current/selectors'
import { AppState } from '../../../store'
import { UprightStyle } from '../../../types'
import { pluralizeFromNumber } from '../../../utils/string'
import { Row } from './Row'
import './style.scss'

interface Props {
  selectedIds: number[]
  onChange: (selectedIds: number[]) => void
  resultSectionHeight?: number
  includeNoInventoryLocation?: boolean
}

const DEFAULT_RESULT_SECTION_HEIGHT = 200
const ESTIMATED_ROW_HEIGHT = 25
const rowHeightCache = new CellMeasurerCache({
  defaultHeight: ESTIMATED_ROW_HEIGHT,
  fixedWidth: true
})

const style: UprightStyle = {
  noMargin: { margin: 0 },
  normalCursor: { cursor: 'default' }
}

export const InventoryLocationMultiselect = ({
  selectedIds,
  onChange,
  resultSectionHeight,
  includeNoInventoryLocation = false
}: Props) => {
  const dispatch = useDispatch()

  const [query, setQuery] = React.useState('')
  const [showSelectedOnly, setShowSelectedOnly] = React.useState(false)
  const [previousSelection, setPreviousSelection] = React.useState(null)

  const { loading, values, error } = useSelector((s: AppState) => s.current.inventoryLocationOptions)

  const options = React.useMemo(() => {
    if (!values) return values
    if (includeNoInventoryLocation && !values.some(option => option.value === -1)) {
      return [{ value: -1, label: '(No location)' }, ...values]
    }
    return values
  }, [values, includeNoInventoryLocation])

  const filterOptionsFunction = useSelector(buildInventoryLocationOptionsIndexNameOnly)

  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const filteredOptions = React.useMemo(() => {
    const queryResults = filterOptionsFunction(options || [], query)
    return showSelectedOnly ? queryResults.filter(option => selectedIds.includes(option.value)) : queryResults
  }, [query, options, filterOptionsFunction, selectedIds, showSelectedOnly])

  const onChangeSearchInput = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setPreviousSelection(null)
    setQuery(e.currentTarget.value)
  }, [])

  const onClearQuery = React.useCallback(() => {
    setQuery('')
    searchInputRef.current.focus()
  }, [setQuery])

  const onToggleRange = React.useCallback(
    (givenStart, givenEnd, checkedOverride = null) => {
      // If some locations in the range are NOT selected, then fill in the selection.
      // Otherwise, deselect locations in the range.
      // need to change this to use a local state to properly support hiding un-selected
      const minMaxIndex = (index: number) => Math.min(filteredOptions.length - 1, Math.max(0, index))
      const range = [givenStart, givenEnd].map(i => parseInt(i) || 0).sort((a, b) => a - b) // Support clicking above initially selected.
      const startIndex = minMaxIndex(range[0])
      const endIndex = minMaxIndex(range[1])
      const idsForRange = filteredOptions.map(option => option.value).slice(startIndex, endIndex + 1)
      const uncheckedIds = idsForRange.filter(id => !selectedIds.includes(id))

      if (checkedOverride === true) {
        onChange([...selectedIds, ...uncheckedIds])
      } else if (checkedOverride === false) {
        onChange(selectedIds.filter(id => !idsForRange.includes(id)))
      } else if (uncheckedIds.length) {
        onChange([...selectedIds, ...uncheckedIds])
      } else {
        onChange(selectedIds.filter(id => !idsForRange.includes(id)))
      }
    },
    [selectedIds, filteredOptions, onChange]
  )

  const onToggleCheckbox = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      // Each row has id of "location-0" where the number is the index of the row
      const index = e.currentTarget.id.split('-').pop()

      // Use bulk select if a different checkbox was already selected AND Shift key is down.
      if (e.shiftKey && previousSelection && previousSelection !== index) {
        // If item was previously changed to checked, bulk check the selection. Uncheck otherwise.
        const shouldCheck = selectedIds.includes(filteredOptions[previousSelection].value)
        onToggleRange(previousSelection, index, shouldCheck)
      } else {
        onToggleRange(index, index)
      }
      setPreviousSelection(index)
    },
    [onToggleRange, selectedIds, filteredOptions, previousSelection, setPreviousSelection]
  )

  const onToggleAll = React.useCallback(() => {
    onToggleRange(0, filteredOptions.length - 1)
  }, [onToggleRange, filteredOptions.length])

  const filteredOptionSelectionCount: 'ALL' | 'SOME' | 'NONE' = React.useMemo(() => {
    const count = filteredOptions.filter(option => selectedIds.includes(option.value)).length
    if (count === filteredOptions.length) return 'ALL'
    if (count === 0) return 'NONE'
    return 'SOME'
  }, [filteredOptions, selectedIds])

  // instead of using a ref here, we useState so the value is available on the first render
  // of the hidden menu
  const [selectionMenuHeight, setSelectionMenuHeight] = React.useState(0)
  const resultsStyle = React.useMemo(
    () => ({
      height: (resultSectionHeight || DEFAULT_RESULT_SECTION_HEIGHT) - selectionMenuHeight
    }),
    [selectionMenuHeight, resultSectionHeight]
  )
  React.useEffect(() => {
    // reload locations the input every mount, but load cached result from current
    dispatch(requestInventoryLocations())
  }, [dispatch])

  const onShowSelected = React.useCallback(() => {
    setShowSelectedOnly(true)
  }, [setShowSelectedOnly])

  const onShowAll = React.useCallback(() => {
    setShowSelectedOnly(false)
  }, [setShowSelectedOnly])

  return (
    <div className={`location-multiselect-container ${error && 'error'}`}>
      <InputGroup
        value={query}
        onChange={onChangeSearchInput}
        leftIcon={IconNames.SEARCH}
        className="location-multiselect-search"
        placeholder="Search by name..."
        inputRef={ref => {
          searchInputRef.current = ref
        }}
        disabled={loading || error != null}
        rightElement={
          <>
            {query && <Button minimal icon={IconNames.CROSS} onClick={onClearQuery} />}
            <Tag minimal>{selectedIds.length} selected</Tag>
          </>
        }
      />
      {(showSelectedOnly || filteredOptionSelectionCount !== 'NONE') && !loading && !error && (
        <div
          className="location-multiselect-filters"
          ref={ref => {
            setSelectionMenuHeight(ref?.offsetHeight || 0)
          }}
        >
          <span className="title-heading">Selection</span>
          {filteredOptionSelectionCount === 'SOME' && (
            <Button
              text="Show selected only"
              minimal
              className="bp5-button-as-link"
              intent={Intent.PRIMARY}
              onClick={onShowSelected}
            />
          )}
          {filteredOptionSelectionCount === 'ALL' && filteredOptions.length > 0 && (
            <Button
              text={showSelectedOnly ? 'Showing selected only' : 'All matches are selected'}
              disabled
              style={style.normalCursor}
              minimal
              className="bp5-button-as-link"
              intent={Intent.NONE}
              onClick={onShowSelected}
            />
          )}
          {showSelectedOnly && (
            <Button
              text={query ? 'Show all matches' : 'Show all'}
              minimal
              className="bp5-button-as-link"
              intent={Intent.PRIMARY}
              onClick={onShowAll}
            />
          )}
        </div>
      )}

      {!loading && !error && (
        <div className="location-multiselect-results" style={resultsStyle}>
          {/* Use parent size to sniff width of enclosing div */}
          <ParentSize>
            {({ width, height }) => (
              <List
                height={height}
                estimatedRowSize={ESTIMATED_ROW_HEIGHT}
                rowHeight={rowHeightCache.rowHeight}
                rowCount={filteredOptions.length}
                width={width}
                deferredMeasurementCache={rowHeightCache}
                rowRenderer={({ style: rowStyle, ...rowProps }) => {
                  const loc = filteredOptions[rowProps.index]
                  return (
                    <CellMeasurer {...rowProps} cache={rowHeightCache}>
                      {({ registerChild }) => {
                        const isOddChild = rowProps.index % 2 !== 0
                        const divClass = ['location-multiselect-striped', isOddChild && 'odd-child']
                          .filter(c => c)
                          .join(' ')
                        return (
                          <div className={divClass} style={rowStyle} ref={registerChild}>
                            <Row
                              index={rowProps.index}
                              label={loc.label}
                              checked={selectedIds.includes(loc.value)}
                              onClick={onToggleCheckbox}
                            />
                          </div>
                        )
                      }}
                    </CellMeasurer>
                  )
                }}
              />
            )}
          </ParentSize>
        </div>
      )}
      {loading && (
        <div className="location-multiselect-loading">
          <Spinner size={SpinnerSize.SMALL} />
        </div>
      )}
      {error && (
        <div className="location-multiselect-error">
          <NonIdealState
            icon={<Icon icon={IconNames.ISSUE} intent={Intent.DANGER} size={IconSize.LARGE} />}
            title="Failed to fetch inventory locations"
            action={
              <Button icon={IconNames.REFRESH} text="Retry" onClick={() => dispatch(requestInventoryLocations())} />
            }
          />
        </div>
      )}
      <div className="location-multiselect-footer">
        <Button
          onClick={onToggleAll}
          small
          disabled={loading || error != null}
          intent={Intent.PRIMARY}
          text={query ? 'Toggle matches' : 'Toggle all'}
          className="bp5-button-as-link"
          minimal
        />
        {!loading && (
          <>
            <div
              className={[filteredOptions.length > 0 && 'non-hover', Classes.TEXT_MUTED].filter(s => s).join(' ')}
            >{`${filteredOptions.length} result${pluralizeFromNumber(filteredOptions.length)}`}</div>
            {filteredOptions.length > 0 && (
              <div className={['hover-only', Classes.TEXT_MUTED].join(' ')}>Shift + click to select multiple</div>
            )}
          </>
        )}
        {loading && <div className={Classes.TEXT_MUTED}>Loading...</div>}
      </div>
    </div>
  )
}
